/* eslint react-hooks/exhaustive-deps: "warn" */
import cn from 'classnames';

import { Link } from 'libs/router/Link';

import { useInt } from 'hooks/useInt';

import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';

import styles from './SalesProspectingLeadGenerationExport.module.scss';

type Row = {
  company: string;
  regNumber: string;
  flag: string;
  country: string;
  code: string;
  codeMap: string;
  status: string;
  statusWarn?: boolean;
  accounts: string;
  turnover: string;
  source: string;
};

const ROWS: Row[] = [
  {
    company: 'Nordlicht Systeme GmbH',
    regNumber: 'HRB 214 887',
    flag: '🇩🇪',
    country: 'Germany',
    code: 'WZ 62.01',
    codeMap: 'NACE 62.01',
    status: 'Active',
    accounts: '31 Dec 2025',
    turnover: '€18.4M',
    source: 'Handelsregister',
  },
  {
    company: 'Vantage Cloudworks B.V.',
    regNumber: 'KvK 78 442 019',
    flag: '🇳🇱',
    country: 'Netherlands',
    code: 'SBI 6201',
    codeMap: 'NACE 62.01',
    status: 'Active',
    accounts: '30 Jun 2025',
    turnover: '€9.1M',
    source: 'KvK',
  },
  {
    company: 'Meridian Data SPRL',
    regNumber: 'BE 0644.812.330',
    flag: '🇧🇪',
    country: 'Belgium',
    code: 'NACE-BEL 62010',
    codeMap: '',
    status: 'Active',
    accounts: '31 Dec 2025',
    turnover: '€6.7M',
    source: 'KBO / BCE',
  },
  {
    company: 'Halden Softwarehaus AG',
    regNumber: 'HRB 91 204',
    flag: '🇩🇪',
    country: 'Germany',
    code: 'WZ 62.01',
    codeMap: 'NACE 62.01',
    status: 'Name changed',
    statusWarn: true,
    accounts: '31 Dec 2025',
    turnover: '€31.2M',
    source: 'Handelsregister',
  },
  {
    company: 'Kadence Applied B.V.',
    regNumber: 'KvK 62 118 774',
    flag: '🇳🇱',
    country: 'Netherlands',
    code: 'SBI 6201',
    codeMap: 'NACE 62.01',
    status: 'Active',
    accounts: '31 Dec 2025',
    turnover: '€12.9M',
    source: 'KvK',
  },
  {
    company: 'Orbit Integrationen GmbH',
    regNumber: 'HRB 178 003',
    flag: '🇩🇪',
    country: 'Germany',
    code: 'WZ 62.01',
    codeMap: 'NACE 62.01',
    status: 'Active',
    accounts: '31 Dec 2024',
    turnover: '€7.3M',
    source: 'Handelsregister',
  },
];

const ArrowIcon = () => (
  <svg width="14" height="14" viewBox="0 0 15 15" fill="none">
    <path
      d="M3 7.5h9M8.5 3.5l4 4-4 4"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const SalesProspectingLeadGenerationExport = () => {
  const { t } = useInt();

  return (
    <section className={styles.section}>
      <div className={styles.container}>
        <div className={styles.marker}>
          <span className={styles.markerNum}>02</span> / {t('Export')}
        </div>

        <div className={styles.body}>
          <div className={styles.head}>
            <TitleBlock
              align="left"
              title={t('What actually lands in your CRM')}
              description={
                <>
                  {t(
                    'Define a segment, press Export, and this is the file. Every row carries the identifier the government issued, the company’s status on the day you pulled it, and the registry each field came from. Your ops team can reconcile it against the source. Your compliance team can defend where it came from.',
                  )}
                </>
              }
            />
          </div>

          <div className={styles.shell}>
            <div className={styles.shellBar}>
              <span className={styles.shellTitle}>{t('Segment export')}</span>
              <span className={styles.shellMeta}>{t('4,812-row segment · first 6 rows')}</span>
              <span className={styles.shellTag}>{t('SAMPLE')}</span>
            </div>

            <div className={styles.scroll}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>{t('Company')}</th>
                    <th>{t('Jurisdiction')}</th>
                    <th>{t('National code')}</th>
                    <th>{t('Status')}</th>
                    <th>{t('Latest accounts')}</th>
                    <th className={styles.right}>{t('Turnover, filed')}</th>
                    <th>{t('Source')}</th>
                  </tr>
                </thead>

                <tbody>
                  {ROWS.map((row) => (
                    <tr key={row.regNumber}>
                      <td>
                        <span className={styles.company}>{row.company}</span>
                        <span className={styles.regNumber}>{row.regNumber}</span>
                      </td>

                      <td>
                        <span className={styles.jurisdiction}>
                          <span className={styles.flag} aria-hidden="true">
                            {row.flag}
                          </span>
                          {row.country}
                        </span>
                      </td>

                      <td className={styles.mono}>
                        {row.code}
                        {row.codeMap && <i>{row.codeMap}</i>}
                      </td>

                      <td>
                        <span className={cn(styles.status, row.statusWarn && styles.statusWarn)}>{row.status}</span>
                      </td>

                      <td className={styles.mono}>{row.accounts}</td>

                      <td className={cn(styles.amount, styles.right)}>{row.turnover}</td>

                      <td className={styles.source}>{row.source}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className={styles.moreData}>
            <p>
              {t(
                'This is an extract. Every record carries far more: ownership, officers, financials, group structure, credit and contacts.',
              )}
            </p>

            <Link className={styles.dictionaryLink} route="/data-dictionary">
              {t('View the full data dictionary')}
              <span className={styles.arrow}>
                <ArrowIcon />
              </span>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};
